import { Component, ErrorInfo, ReactNode } from "react";

interface Props {
  /** Etiqueta para identificar dónde ocurrió (aparece en consola y pantalla). */
  label?: string;
  children: ReactNode;
}

interface State {
  error: Error | null;
  info: ErrorInfo | null;
}

/**
 * Error boundary de DIAGNÓSTICO (temporal): a diferencia del ErrorBoundary
 * global, SIEMPRE muestra el mensaje + stack en pantalla (también en prod) y lo
 * loguea en consola. Sirve para capturar crashes que en prod sólo dejan pantalla
 * en blanco. Quitar cuando se resuelva el bug.
 */
class DebugErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ info });
    // SIEMPRE (también en prod) para poder capturarlo desde la consola.
    // eslint-disable-next-line no-console
    console.error(
      `🔴 [DebugErrorBoundary${this.props.label ? ` · ${this.props.label}` : ""}]`,
      error,
      info.componentStack,
    );
  }

  render() {
    const { error, info } = this.state;
    if (error) {
      return (
        <div className="p-4 m-2 rounded-lg border-2 border-red-300 bg-red-50 text-red-900 text-xs">
          <p className="font-bold mb-1">
            🔴 Error capturado{this.props.label ? ` en ${this.props.label}` : ""}
          </p>
          <pre className="whitespace-pre-wrap break-words max-h-64 overflow-auto">
            {error.name}: {error.message}
            {"\n\n"}
            {error.stack}
            {"\n\n--- Component stack ---"}
            {info?.componentStack}
          </pre>
          <button
            type="button"
            onClick={() => this.setState({ error: null, info: null })}
            className="mt-2 px-3 py-1 rounded bg-red-600 text-white"
          >
            Reintentar
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default DebugErrorBoundary;
